import { Form } from 'antd';
import dayjs from 'dayjs';
import React, { FC, memo, Suspense, useCallback, useEffect, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Button } from 'shared/ui/Button';
import { ModernDatePicker as DatePicker } from 'shared/ui/ModernDatePicker';
import { ModernInput as Input } from 'shared/ui/ModernInput';
import { CLIENT_DATE_FORMAT, languageToLabelMap } from 'shared/utils/constants';
import { AppLanguage } from 'app/config/i18Config/types';
import { ModernSelect as Select } from 'shared/ui/ModernSelect';
import { datePickerValueUnder18, disableFutureDatesUnder18 } from 'shared/utils/helpers/disable18YearsDatePicker';
import { ChangeUserDetails } from 'features/ChangeUserDetails';
import { TextArea } from 'shared/ui/TextArea';
import { UserAccountType, useRole, UserRoles } from 'entities/User';
import { getGlobalSettings } from 'app/appState/model/selectors/getGlobalSettings';
import { ModernPhoneInput as PhoneInput } from 'shared/ui/ModernPhoneInput';
import { PHONE_NUMBER_REGEX } from 'shared/utils/regex';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { Modal } from 'shared/ui/Modal';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { PageLoader } from 'features/PageLoader';

interface CustomerDetailsFormProps {
  userId: string;
  language: string;
  email: string;
  dateOfBirth?: Nullable<string>;
  additionalEmail?: Nullable<string>;
  additionalPhone?: Nullable<string>;
  phone?: Nullable<string>;
  bankingRequisites?: Nullable<string>;
  country: Nullable<string>;
  city: Nullable<string>;
  street: Nullable<string>;
  zip: Nullable<string>;
  house: Nullable<string>;
  apartment: Nullable<string>;
  firstName: string;
  lastName: string;
  accountType: UserAccountType;
  company?: Nullable<string>;
  role: UserRoles;
  identificationNumber?: Nullable<string>;
}

export const CustomerDetailsForm: FC<CustomerDetailsFormProps> = memo((props) => {
  const {
    userId,
    dateOfBirth,
    email,
    additionalEmail,
    bankingRequisites,
    language,
    phone,
    additionalPhone,
    zip,
    city,
    apartment,
    country,
    house,
    street,
    firstName,
    lastName,
    accountType,
    company,
    identificationNumber,
  } = props;

  const { t } = useAppTranslation(['profile', 'common']);

  const globalSettings = useAppSelector(getGlobalSettings);

  const { isPartner, isManagement } = useRole();

  const { isDesktop } = useScreenBreakpoints();

  const [form] = Form.useForm();
  const [isModalOpened, setModalOpened] = useState<boolean>(false);

  const handleCloseModal = useCallback((): void => {
    setModalOpened(false);
  }, []);

  const handleOpenModal = useCallback((): void => {
    setModalOpened(true);
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      language,
      additionalEmail,
      additionalPhone,
      bankingRequisites,
      dateOfBirth: dateOfBirth ? dayjs(dateOfBirth) : undefined,
      firstName,
      lastName,
      email,
      phone,
      accountType,
      company,
      identificationNumber,
      zip,
      city,
      apartment,
      country,
      house,
      street,
    });
  }, [
    additionalEmail,
    dateOfBirth,
    form,
    language,
    bankingRequisites,
    additionalPhone,
    firstName,
    lastName,
    email,
    phone,
    accountType,
    company,
    identificationNumber,
    zip,
    city,
    apartment,
    country,
    house,
    street,
  ]);

  const languages = globalSettings?.availableLanguages
    .filter((lang: string): lang is AppLanguage => Object.values(AppLanguage).includes(lang as AppLanguage))
    .map((language: AppLanguage) => ({
      label: languageToLabelMap[language],
      value: language,
    }));

  const fullAddress = street ? `${street || ''}, ${house || ''}, ${apartment || ''}` : '-';

  return (
    <>
      <div className="flex flex-col p-4 bg-white rounded-lg gap-2">
        <div className="text-2xl flex font-semibold font-tektur">{t('Customer details')}</div>
        <div className="flex flex-col">
          <div className="text-sm text-grey">{t('Additional Email')}</div>
          <div className="text-lg text-primaryLight font-tektur">{additionalEmail || '-'}</div>
        </div>
        <div className="flex flex-col">
          <div className="text-sm text-grey">{t('Additional phone')}</div>
          <div className="text-lg text-primaryLight font-tektur">{additionalPhone || '-'}</div>
        </div>
        <div className="flex flex-col">
          <div className="text-sm text-grey">{t('Date of Birth')}</div>
          <div className="text-lg text-primaryLight font-tektur">{getClientDateFormat(dateOfBirth) || '-'}</div>
        </div>
        <div className="flex flex-col">
          <div className="text-sm text-grey">{t('Language')}</div>
          <div className="text-lg text-primaryLight font-tektur">{languageToLabelMap[language as AppLanguage] || '-'}</div>
        </div>
        <div className="flex flex-col">
          <div className="text-sm text-grey">{t('Banking requisites')}</div>
          <div className="text-lg text-primaryLight font-tektur">{bankingRequisites || '-'}</div>
        </div>
        <div className="flex flex-col">
          <div className="text-sm text-grey">{t('Country')}</div>
          <div className="text-lg text-primaryLight font-tektur">{country || '-'}</div>
        </div>
        <div className="flex flex-col">
          <div className="text-sm text-grey">{t('Settlement')}</div>
          <div className="text-lg text-primaryLight font-tektur">{city || '-'}</div>
        </div>
        <div className="flex flex-col">
          <div className="text-sm text-grey">{t('Address')}</div>
          <div className="text-lg text-primaryLight font-tektur">{fullAddress}</div>
        </div>
        <div className="flex flex-col">
          <div className="text-sm text-grey">{t('Zip code')}</div>
          <div className="text-lg text-primaryLight font-tektur">{zip || '-'}</div>
        </div>

        <Button onClick={handleOpenModal}>{t('Edit', { ns: 'common' })}</Button>
      </div>
      <Suspense fallback={<PageLoader />}>
        <Modal
          isOpen={isModalOpened}
          onClose={handleCloseModal}
          footer={false}
          width={isDesktop ? 780 : 400}
          isMobile={!isDesktop}
          centered={!!isDesktop}
          closeIcon={isDesktop}
        >
          <div className="text-2xl desktop:px-2 font-tektur mb-6 font-semibold">{t('Customer details')}</div>
          <Form className="flex flex-col desktop:px-2" name="customerDetailsForm" form={form} layout="vertical" disabled={isPartner}>
            <div className="desktop:flex desktop:space-x-7">
              <Form.Item
                label={<span className="text-grey p-0">{t('Account type')}</span>}
                name="accountType"
                className="desktop:w-1/2"
                labelCol={{ style: { paddingBottom: 0 } }}
                rules={[{ required: true, message: t('Please, select account type') }]}
              >
                <Select
                  placeholder={t('Select account type')}
                  bordered
                  size="small"
                  disabled={!isManagement}
                  options={[
                    { label: t('Personal'), value: UserAccountType.PERSONAL },
                    { label: t('Business'), value: UserAccountType.BUSINESS },
                  ]}
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prevValues, nextValues) => prevValues.accountType !== nextValues.accountType}>
                {({ getFieldValue }) => {
                  const isBusinessAccountType = getFieldValue('accountType') === UserAccountType.BUSINESS;

                  return isBusinessAccountType ? (
                    <Form.Item
                      label={<span className="text-grey">{t('Company')}</span>}
                      className="desktop:w-1/2"
                      name="company"
                      labelCol={{ style: { paddingBottom: 0 } }}
                      rules={[
                        { required: true, message: t('Please enter company!') },
                        {
                          min: 1,
                          max: 100,
                          message: t('Company name must have 1 - 100 characters!'),
                        },
                      ]}
                    >
                      <Input placeholder={t('Enter company')} bordered size="small" disabled={!isManagement} />
                    </Form.Item>
                  ) : (
                    <div className="w-1/2" />
                  );
                }}
              </Form.Item>
            </div>

            <div className="desktop:flex items-center desktop:space-x-7">
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="desktop:w-full"
                name="firstName"
                label={<span className="text-grey">{t('Name')}</span>}
                rules={[{ required: true, message: t('Please enter your first name!') }]}
              >
                <Input bordered placeholder={t('Enter first name')} size="small" disabled={!isManagement} />
              </Form.Item>
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="lastName"
                label={<span className="text-grey">{t('Last Name')}</span>}
                rules={[{ required: true, message: t('Please enter your last name!') }]}
              >
                <Input bordered placeholder={t('Enter last name')} size="small" disabled={!isManagement} />
              </Form.Item>
            </div>

            <div className="desktop:flex items-center desktop:space-x-7">
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="email"
                label={<span className="text-grey">{t('Email')}</span>}
                rules={[
                  { required: true, message: t('Please enter your email!') },
                  { type: 'email', message: t('Please enter correct email!') },
                ]}
              >
                <Input bordered placeholder={t('Enter email')} size="small" disabled={!isManagement} />
              </Form.Item>
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="phone"
                label={<span className="text-grey">{t('Phone')}</span>}
                rules={[
                  { required: true, message: t('Please enter your phone!') },
                  { pattern: PHONE_NUMBER_REGEX, message: t('Please provide a valid phone number. Example: +123456789012345') },
                ]}
              >
                <PhoneInput placeholder={t('Enter phone')} bordered initialValue={phone} disabled={!isManagement} size="small" />
              </Form.Item>
            </div>
            <div className="desktop:flex items-center desktop:space-x-7">
              <Form.Item noStyle shouldUpdate={(prevValues, nextValues) => prevValues.accountType !== nextValues.accountType}>
                {({ getFieldValue }) => {
                  const isBusinessAccountType = getFieldValue('accountType') === UserAccountType.BUSINESS;

                  const identificationNumberLabel = t(
                    isBusinessAccountType ? 'Business identification number' : 'Personal identification number',
                  );

                  return (
                    <Form.Item
                      labelCol={{ style: { paddingBottom: 0 } }}
                      className="desktop:w-1/2"
                      name="identificationNumber"
                      label={<span className="text-grey">{identificationNumberLabel}</span>}
                    >
                      <Input bordered placeholder={identificationNumberLabel} size="small" disabled={!isManagement} />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Form.Item
                className="desktop:w-1/2"
                name="language"
                labelCol={{ style: { paddingBottom: 0 } }}
                label={<span className="text-grey">{t('Language')}</span>}
                rules={[{ required: true, message: t('Please select language!') }]}
              >
                <Select placeholder={t('Select language')} options={languages || []} bordered size="small" />
              </Form.Item>
            </div>

            <div className="desktop:flex items-center desktop:space-x-7">
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="additionalEmail"
                rules={[
                  { type: 'email', message: t('Please enter a correct additional email!') },
                  {
                    validator: async (_, value) => {
                      value && value === email
                        ? await Promise.reject(new Error(t('Additional email must be different from the main email!')))
                        : await Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  bordered
                  placeholder={t('Additional email')}
                  tooltip={t('Please enter an additional email address to receive copies of bills')}
                  label={t('Additional Email')}
                  size="small"
                  containerClassName="text-grey"
                  labelClassName="!mb-0"
                />
              </Form.Item>
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="desktop:w-full"
                name="additionalPhone"
                label={<span className="text-grey">{t('Additional phone')}</span>}
                rules={[
                  {
                    pattern: PHONE_NUMBER_REGEX,
                    message: t('Please provide a valid phone number. Example: +123456789012345'),
                  },
                  {
                    validator: async (_, value) => {
                      value && value === phone
                        ? await Promise.reject(new Error(t('Additional phone must be different from the main phone!')))
                        : await Promise.resolve();
                    },
                  },
                ]}
              >
                <PhoneInput placeholder={t('Enter phone')} bordered initialValue={additionalPhone} size="small" />
              </Form.Item>
            </div>
            <div className="desktop:flex items-center desktop:space-x-7">
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="dateOfBirth"
                label={<span className="text-grey">{t('Date of Birth')}</span>}
              >
                <DatePicker
                  bordered
                  className="h-[52px] w-full"
                  format={CLIENT_DATE_FORMAT}
                  disabledDate={disableFutureDatesUnder18}
                  defaultPickerValue={datePickerValueUnder18}
                  size="small"
                  disabled={!isManagement}
                />
              </Form.Item>

              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="bankingRequisites"
                label={<span className="text-grey">{t('Banking requisites')}</span>}
              >
                <TextArea bordered placeholder={t('Banking requisites')} rows={1} />
              </Form.Item>
            </div>

            <div className="desktop:flex items-center desktop:space-x-7">
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="country"
                label={<span className="text-grey">{t('Country')}</span>}
                rules={[{ required: true, message: t('Please enter your country!') }]}
              >
                <Input bordered placeholder={t('Enter country')} size="small" />
              </Form.Item>
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="city"
                label={<span className="text-grey">{t('Settlement')}</span>}
                rules={[{ required: true, message: t('Please enter your city!') }]}
              >
                <Input bordered placeholder={t('Settlement')} size="small" />
              </Form.Item>
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="zip"
                label={<span className="text-grey">{t('Zip code')}</span>}
                /*   rules={[{ required: true, message: t('Please enter your zip code!') }]} */
              >
                <Input bordered placeholder={t('Enter zip code')} size="small" />
              </Form.Item>
            </div>
            <div className="desktop:flex items-center desktop:space-x-7">
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="street"
                label={<span className="text-grey">{t('Street')}</span>}
                rules={[{ required: true, message: t('Please enter your street!') }]}
              >
                <Input bordered placeholder={t('Enter street')} size="small" />
              </Form.Item>
              <Form.Item
                className="w-full"
                name="house"
                labelCol={{ style: { paddingBottom: 0 } }}
                label={<span className="text-grey">{t('House')}</span>}
                rules={[{ required: true, message: t('Please enter your house!') }]}
              >
                <Input bordered placeholder={t('Enter house')} size="small" />
              </Form.Item>
              <Form.Item
                labelCol={{ style: { paddingBottom: 0 } }}
                className="w-full"
                name="apartment"
                label={<span className="text-grey">{t('Apartment')}</span>}
              >
                <Input bordered placeholder={t('Enter apartment')} size="small" />
              </Form.Item>
            </div>

            <div className="desktop:flex justify-end gap-4">
              {!isDesktop && (
                <Button theme="secondary" onClick={handleCloseModal} containerClassName="w-full mb-4">
                  {t('Cancel', { ns: 'common' })}
                </Button>
              )}
              <ChangeUserDetails
                userId={userId}
                formInstance={form}
                onSuccess={handleCloseModal}
                action={
                  <Button isDisabled={isPartner} containerClassName="w-full">
                    {t('Save', { ns: 'common' })}
                  </Button>
                }
              />
            </div>
          </Form>
        </Modal>
      </Suspense>
    </>
  );
});
